/** @format */

import {Fragment, useRef, useState} from 'react';

import {Box, Typography} from '@mui/material';

import {Trans, useTranslation} from 'react-i18next';

import Modal from 'components/Modal';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import TertiaryMediumButton from 'components/buttons/TertiaryMediumButton';

import {useMixpanelContext} from 'contexts/MixpanelContext';
import {useCompanyContext} from 'contexts/CompanyContext';

import {ChartIds} from '../../models/chartIds';
import {Space} from '../../models/spaces';
import {WidgetBlock, WidgetBlockId} from '../../models/widgets';
import DropdownButton from './components/dropdown-button';
import SubMenu from './components/sub-menu';
import WidgetsBlock from './components/widgets-block';
import {getNewBoardWidgets} from './helpers/getNewBoardWidgets';
import removeMenuWidget from './helpers/removeMenuWidget';
import updateBoardWidgets from './helpers/updateBoardWidgets';
import updateChartSelected from './helpers/updateChartSelected';
import isStubChartId from '../../helpers/isStubChartId';
import {ButtonsBox, DropdownRoot as Root, dropdownClasses as classes} from './style';

interface Props {
  onCloseDropdown: () => void;
  onConfirmMenuSettings: (space: Space) => void;
  onRemoveSpace: () => void;
  space: Space;
  widgetBlocks: WidgetBlock[];
}

const Dropdown = ({
  onCloseDropdown,
  space,
  onConfirmMenuSettings,
  onRemoveSpace,
  widgetBlocks,
}: Props) => {
  const [subMenuId, setSubMenuId] = useState<WidgetBlockId | null>(null);
  const [top, setTop] = useState<string>('0');
  const [innerSpace, setInnerSpace] = useState<Space>({...space});
  const [confirmModal, setConfirmModal] = useState<boolean>(false);

  const rootRef = useRef(null);

  const {t} = useTranslation();
  const {mixpanel} = useMixpanelContext();
  const {currentCompany} = useCompanyContext();

  const selectedBlockData = widgetBlocks.find(block => block.id === subMenuId);

  const openSubMenu = Boolean(selectedBlockData);

  const handleOpenSubMenu = (id: WidgetBlockId) => {
    const rootElement = rootRef.current ? (rootRef.current as HTMLDivElement) : null;
    if (rootElement) {
      setTop(`${rootElement.scrollTop}px`);
    }
    setSubMenuId(id);
  };

  const handleCloseSubMenu = () => {
    setSubMenuId(null);
  };

  const handleConfirmSubMenuSettings = (selected: ChartIds[]) => {
    setInnerSpace(prev => {
      // Текущий редактируемый блок
      const currentBlock = selectedBlockData;
      const blockIds = currentBlock?.subMenu?.widgets || [];
      const blockSet = new Set(blockIds);

      // Выбранные пользователем показатели для этого блока
      const selectedForBlock = selected.filter(id => blockSet.has(id));

      // Меню: сохраняем все показатели вне этого блока и подменяем показатели блока выбранными
      const prevMenu = prev.widgetsMenuSelected;
      const keepOthers = prevMenu.filter(id => !blockSet.has(id));
      const newWidgetsMenuSelected = [...keepOthers, ...selectedForBlock];

      // Доска: обновляем только показатели текущего блока, остальные сохраняем
      const currentBoardOthers = prev.widgetsBoardSelected.filter(id => !blockSet.has(id));
      const newBoardForBlock = getNewBoardWidgets({
        oldSelections: prevMenu.filter(id => blockSet.has(id)),
        newSelections: selectedForBlock,
        currentList: prev.widgetsBoardSelected.filter(id => blockSet.has(id)),
      });

      return {
        ...prev,
        widgetsMenuSelected: newWidgetsMenuSelected,
        widgetsBoardSelected: [...newBoardForBlock, ...currentBoardOthers],
      };
    });
    handleCloseSubMenu();
  };

  const handleChangeSelection = (chartId: ChartIds) => {
    setInnerSpace(prev =>
      updateBoardWidgets({
        data: prev,
        chartId,
      }),
    );
  };

  const handleAddCrWidget = () => {
    const newId = `funnel_cr_${Date.now()}` as ChartIds;
    let next: Space | undefined;
    setInnerSpace(prev => {
      if (prev.widgetsMenuSelected.includes(newId) || prev.widgetsBoardSelected.includes(newId)) {
        next = prev;
        return prev;
      }
      next = {
        ...prev,
        widgetsMenuSelected: [...prev.widgetsMenuSelected, newId],
        widgetsBoardSelected: [...prev.widgetsBoardSelected, newId],
        widgetsSettings: {
          ...prev.widgetsSettings,
          [newId]: {calcMethod: 'day'},
        },
      };
      return next!;
    });
    return next;
  };

  const handleRemoveSubMenuItem = (chartId: ChartIds) => {
    setInnerSpace(prev =>
      removeMenuWidget({
        data: prev,
        removeId: chartId,
      }),
    );
  };

  const handleOpenModal = () => {
    setConfirmModal(true);
  };

  const handleCloseModal = () => {
    setConfirmModal(false);
  };

  const handleConfirmRemove = () => {
    handleCloseModal();
    onRemoveSpace();
  };

  const handleConfirmMenuSettings = (maybeSpace?: Space) => {
    // Перед тем, как отправлять новую версию расположения галочек, нужно переназначить положения свитчеров
    // Подробнее https://adsensor-ru.atlassian.net/browse/AS-6094
    const base = maybeSpace || innerSpace;

    const stubLayers = base.widgetsBoardSelected.filter(isStubChartId);
    if (stubLayers.length > 0) {
      mixpanel.track('select_stub_layers', {
        project_uuid: currentCompany?.uuid,
        stub_layers: stubLayers,
      });
    }

    onConfirmMenuSettings(updateChartSelected(base, space));
  };

  return (
    <Root lockScroll={openSubMenu} data-testid='dropdown'>
      <Box className={classes.scrollable}>
        <ButtonsBox>
          <Typography className={classes.removeButtonLabel}>
            <Trans>Настройка пространства</Trans>
          </Typography>
          <DropdownButton
            variant='red'
            onClick={handleOpenModal}
            testId='dropdown-remove-space'
            sx={{width: 'max-content', height: '30px'}}
          >
            <Trans>Удалить</Trans>
          </DropdownButton>
        </ButtonsBox>

        <Box className={classes.divider} />

        {widgetBlocks.map((block, index, arr) => {
          const isLast = index === arr.length - 1;
          return (
            <Fragment key={block.id}>
              <WidgetsBlock
                commonWidgets={block.commonWidgets || []}
                subMenu={block.subMenu}
                label={block.label}
                blockId={block.id}
                space={innerSpace}
                onClickAdd={() => handleOpenSubMenu(block.id)}
                onChangeSelection={handleChangeSelection}
                onRemove={handleRemoveSubMenuItem}
                onConfirmMenuSettings={handleConfirmMenuSettings}
                onAddCrWidget={handleAddCrWidget}
              />
              {!isLast && <Box className={classes.divider} />}
            </Fragment>
          );
        })}

        <SubMenu
          data={selectedBlockData}
          top={top}
          space={innerSpace}
          onClose={handleCloseSubMenu}
          onConfirm={handleConfirmSubMenuSettings}
        />
      </Box>

      <ButtonsBox>
        <DropdownButton
          variant='primary'
          onClick={() => handleConfirmMenuSettings()}
          testId='dropdown-accept'
        >
          <Trans>Применить</Trans>
        </DropdownButton>
        <DropdownButton variant='secondary' onClick={onCloseDropdown} testId='dropdown-decline'>
          <Trans>Отмена</Trans>
        </DropdownButton>
      </ButtonsBox>

      {confirmModal && (
        <Modal header={t('Внимание!')} onClose={handleCloseModal}>
          <Typography className={classes.removeModalMessage}>
            <Trans>Вы действительно хотите удалить рабочее пространство</Trans>
            <br />
            &#34;{innerSpace.title}&#34;?
          </Typography>

          <Box className={classes.removeModalButtons} data-testid='dropdown-modal'>
            <TertiaryMediumButton onClick={handleCloseModal}>
              <Trans>Отмена</Trans>
            </TertiaryMediumButton>
            <PrimaryMediumButton sx={{width: 160}} onClick={handleConfirmRemove}>
              <Trans>Да</Trans>
            </PrimaryMediumButton>
          </Box>
        </Modal>
      )}
    </Root>
  );
};

export default Dropdown;
